import type {
  ClearAll,
  Only,
  SelectAll,
} from '@web-platform/shared-universal-constants/src/airlinesFilter';
import type { MultipleKey } from '@web-platform/shared-universal-constants/src/carrierTypes';
import type {
  DepartureTimesFilterChangedAction,
  DurationFilterChangedAction,
  MixedFilterChangedAction,
  ResetFiltersAction,
  AirportFilterChangedAction,
  LayoverDurationFilterChangedAction,
  LayoverAirportFilterChangedAction,
  LayoverAirportsSelectAllAction,
  LayoverAirportsClearAllAction,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  AllianceFilterChangedAction,
  BaggageFilterChangedAction,
  CarrierControlFilterChangedAction,
  CarriersFilterChangedAction,
  ChangeHotelFilterAction,
  EcoFilterChangedAction,
  PlaceResultLoadedEventEmittedAction,
  StopsFilterChangedAction,
} from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { CarrierSnapshotEntry } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/carriers';
import type { EntityId, SkyNodeCode } from '@web-platform/shared-universal-types-utils/src/utils';

export const AIRPORT_FILTER_CHANGED = 'AIRPORT_FILTER_CHANGED';
export const ALLIANCE_FILTER_CHANGED = 'ALLIANCE_FILTER_CHANGED';
export const BAGGAGE_FILTER_CHANGED = 'BAGGAGE_FILTER_CHANGED';
export const CARRIERS_FILTER_CHANGED = 'CARRIERS_FILTER_CHANGED';
export const CARRIER_CONTROL_FILTER_CHANGED = 'CARRIER_CONTROL_FILTER_CHANGED';
export const DEPARTURE_TIMES_FILTER_CHANGED = 'DEPARTURE_TIMES_FILTER_CHANGED';
export const DURATION_FILTER_CHANGED = 'DURATION_FILTER_CHANGED';
export const ECO_FILTER_CHANGED = 'ECO_FILTER_CHANGED';
export const ECO_FILTER_DISABLED = 'ECO_FILTER_DISABLED';
export const FILTERS_RESET = 'FILTERS_RESET';
export const HOTEL_FILTER_CHANGED = 'HOTEL_FILTER_CHANGED';
export const MIXED_AIRPORT_FILTER_CHANGED = 'MIXED_AIRPORT_FILTER_CHANGED';
export const PLACE_RESULT_LOADED_EVENT_EMITTED = 'PLACE_RESULT_LOADED_EVENT_EMITTED';
export const STOPS_FILTER_CHANGED = 'STOPS_FILTER_CHANGED';
export const LAYOVER_DURATION_FILTER_CHANGED = 'LAYOVER_DURATION_FILTER_CHANGED';
export const LAYOVER_AIRPORT_FILTER_CHANGED = 'LAYOVER_AIRPORT_FILTER_CHANGED';
export const LAYOVER_AIRPORTS_SELECT_ALL = 'LAYOVER_AIRPORTS_SELECT_ALL';
export const LAYOVER_AIRPORTS_CLEAR_ALL = 'LAYOVER_AIRPORTS_CLEAR_ALL';

export const airportFilterChanged = (airport: {
  entityId: EntityId;
  skyNodeCode: SkyNodeCode;
  value: boolean;
}): AirportFilterChangedAction => ({
  type: AIRPORT_FILTER_CHANGED,
  payload: airport,
});

export const departureTimesFilterChanged = (
  legIndex: number,
  values: {
    start: number;
    end: number;
  },
): DepartureTimesFilterChangedAction => ({
  type: DEPARTURE_TIMES_FILTER_CHANGED,
  payload: {
    legIndex,
    ...values,
  },
});

export const durationFilterChanged = (value: number): DurationFilterChangedAction => ({
  type: DURATION_FILTER_CHANGED,
  payload: {
    value,
  },
});

export const filtersReset = (): ResetFiltersAction => ({
  type: FILTERS_RESET,
});

export const mixedAirportFilterChanged = (value: boolean): MixedFilterChangedAction => ({
  type: MIXED_AIRPORT_FILTER_CHANGED,
  payload: {
    value,
  },
});

export const layoverDurationFilterChanged = (
  min: number | null,
  max: number | null,
): LayoverDurationFilterChangedAction => ({
  type: LAYOVER_DURATION_FILTER_CHANGED,
  payload: { min, max },
});

export const layoverAirportFilterChanged = (
  placeEntityId: EntityId,
  excluded: boolean,
): LayoverAirportFilterChangedAction => ({
  type: LAYOVER_AIRPORT_FILTER_CHANGED,
  payload: { placeEntityId, excluded },
});

export const layoverAirportsSelectAll = (): LayoverAirportsSelectAllAction => ({
  type: LAYOVER_AIRPORTS_SELECT_ALL,
});

export const layoverAirportsClearAll = (
  placeEntityIds: EntityId[],
): LayoverAirportsClearAllAction => ({
  type: LAYOVER_AIRPORTS_CLEAR_ALL,
  payload: { placeEntityIds },
});

export const placeResultLoadedEventEmitted = (): PlaceResultLoadedEventEmittedAction => ({
  type: PLACE_RESULT_LOADED_EVENT_EMITTED,
});

export const ecoFilterChanged = (checked: boolean): EcoFilterChangedAction => ({
  type: ECO_FILTER_CHANGED,
  payload: { checked },
});
export const baggageFilterChanged = (values: any): BaggageFilterChangedAction => ({
  type: BAGGAGE_FILTER_CHANGED,
  payload: values,
});
export const stopsFilterChanged = (values: {
  direct: boolean;
  oneStop: boolean;
  twoPlusStops: boolean;
}): StopsFilterChangedAction => ({
  type: STOPS_FILTER_CHANGED,
  payload: values,
});
export const allianceFilterChanged = (alliance: {
  name: string;
  value: boolean;
}): AllianceFilterChangedAction => ({
  type: ALLIANCE_FILTER_CHANGED,
  payload: alliance,
});
export const carrierControlFilterChanged = (
  payload:
    | { action: Only; tokens: string[]; onlyToken: string }
    | { action: ClearAll | SelectAll; tokens: string[] },
): CarrierControlFilterChangedAction => ({
  type: CARRIER_CONTROL_FILTER_CHANGED,
  payload,
});
export const carriersFilterChanged = ({
  aliases,
  id,
  snapshot,
  token,
  value,
}: {
  id: number | MultipleKey;
  token: string;
  aliases?: string[];
  value: boolean;
  snapshot?: CarrierSnapshotEntry[];
}): CarriersFilterChangedAction => ({
  type: CARRIERS_FILTER_CHANGED,
  payload: {
    id,
    token,
    value,
    ...(aliases !== undefined && { aliases }),
    ...(snapshot !== undefined && { snapshot }),
  },
});

export const hotelFilterChanged = (
  filterName: string,
  pillIdentifier: string,
  selectedOptionIndex: number,
): ChangeHotelFilterAction => ({
  type: HOTEL_FILTER_CHANGED,
  payload: {
    filterName,
    pillIdentifier,
    selectedOptionIndex,
  },
});
